import { useUser } from "@clerk/clerk-react";
import {
	ArrowLeft,
	CheckCircle,
	CircleOff,
	Clock,
	Copy,
	ExternalLink,
	QrCode,
	Share2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../lib/apiClient";

interface Payment {
	id: string;
	name: string;
	description?: string;
	amount: number;
	currency: string;
	category?: string;
	paymentUrl: string;
	status: string;
	createdAt: string;
	expiresAt: string;
	failureReason: string | null;
}

export default function PaymentDetails() {
	const navigate = useNavigate();
	const params = useParams();
	const { user } = useUser();
	const paymentId = params.id;

	const [payment, setPayment] = useState<Payment | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
	const [copySuccess, setCopySuccess] = useState(false);

	useEffect(() => {
		if (!paymentId || !user) return;

		const fetchPayment = async () => {
			try {
				setIsLoading(true);
				const response = await apiClient().get<Payment>(`/payments/${paymentId}/owner`);
				setPayment(response.data);

				// Generate QR code
				generateQRCode(response.data.paymentUrl);
				// biome-ignore lint/suspicious/noExplicitAny: error needs to be any
			} catch (err: any) {
				console.error("Failed to fetch payment:", err);
				setError(err.response?.data?.message || "Failed to load payment details");
			} finally {
				setIsLoading(false);
			}
		};

		fetchPayment();
	}, [paymentId, user]);

	const generateQRCode = async (url: string) => {
		try {
			// Use a QR code library or API to generate QR code
			// For now, we'll use a simple online service
			const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
			setQrCodeDataUrl(qrApiUrl);
		} catch (error) {
			console.error("Failed to generate QR code:", error);
		}
	};

	const copyToClipboard = async () => {
		if (!payment) return;

		try {
			await navigator.clipboard.writeText(payment.paymentUrl);
			setCopySuccess(true);
			setTimeout(() => setCopySuccess(false), 2000);
		} catch (error) {
			console.error("Failed to copy to clipboard:", error);
		}
	};

	const sharePayment = async () => {
		if (!payment) return;

		const shareData = {
			title: `Payment Link: ${payment.name}`,
			text: `You have a payment link for ${payment.name} - £${(payment.amount / 100).toFixed(2)}`,
			url: payment.paymentUrl,
		};

		try {
			if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
				await navigator.share(shareData);
			} else {
				// Fallback to copying URL
				await copyToClipboard();
			}
		} catch (error) {
			console.error("Error sharing:", error);
			await copyToClipboard();
		}
	};

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
					<p className="text-gray-600">Loading payment details...</p>
				</div>
			</div>
		);
	}

	if (error || !payment) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="text-center">
					<p className="text-red-600 mb-4">{error || "Payment not found"}</p>
					<button
						type="button"
						onClick={() => navigate("/")}
						className="text-orange-600 hover:text-orange-700"
					>
						Return to Dashboard
					</button>
				</div>
			</div>
		);
	}

	const formatAmount = (amount: number) => {
		return `£${(amount / 100).toFixed(2)}`;
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-GB", {
			day: "numeric",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
				{/* Header */}
				<div className="max-w-2xl mx-auto mb-6 sm:mb-8">
					<div className="flex items-center gap-3 sm:gap-4">
						<button
							type="button"
							onClick={() => navigate("/")}
							className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors p-2 sm:p-0 -ml-2 sm:ml-0 touch-manipulation"
						>
							<ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
							<span className="hidden sm:inline">Back to Dashboard</span>
						</button>
						<h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Payment</h1>
					</div>
				</div>

				{/* Success Message */}
				<div className="max-w-2xl mx-auto mb-6 sm:mb-8">
					{payment.status === "failed" ? (
						<div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
							<CircleOff className="w-6 h-6 text-red-600 flex-shrink-0" />
							<div>
								<h3 className="font-semibold text-red-800 text-sm sm:text-base">
									The payment has failed
								</h3>
								<p className="text-red-700 text-xs sm:text-sm">
									The customer has not completed this payment, this payment link is now voided.
								</p>
							</div>
						</div>
					) : (
						<div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
							<CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
							<div>
								<h3 className="font-semibold text-green-800 text-sm sm:text-base">
									Payment Link Created!
								</h3>
								<p className="text-green-700 text-xs sm:text-sm">
									Your customer can now pay using the link or QR code below.
								</p>
							</div>
						</div>
					)}
				</div>

				{/* Payment Details */}
				<div className="max-w-2xl mx-auto">
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
						{/* Payment Info */}
						<div className="mb-6 sm:mb-8">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 break-words">
								{payment.name}
							</h2>
							{payment.description && (
								<p className="text-gray-600 mb-4 text-sm sm:text-base break-words">
									{payment.description}
								</p>
							)}

							<div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 text-sm text-gray-500 mb-6">
								<div className="flex items-center gap-2">
									<span className="text-2xl sm:text-3xl font-bold text-gray-900">
										{formatAmount(payment.amount)}
									</span>
								</div>
								<div className="flex items-center gap-2">
									<Clock className="w-4 h-4 flex-shrink-0" />
									<span className="text-xs sm:text-sm">
										Expires {formatDate(payment.expiresAt)}
									</span>
								</div>
							</div>
						</div>

						{/* QR Code */}
						<div className="text-center mb-6 sm:mb-8">
							<h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Scan to Pay</h3>
							{qrCodeDataUrl ? (
								<div className="inline-block p-3 sm:p-4 bg-white rounded-lg shadow-sm border border-gray-200">
									<img
										src={qrCodeDataUrl}
										alt="Payment QR Code"
										className="w-40 h-40 sm:w-48 sm:h-48 mx-auto"
									/>
								</div>
							) : (
								<div className="w-40 h-40 sm:w-48 sm:h-48 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
									<QrCode className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
								</div>
							)}
						</div>

						{/* Actions */}
						<div className="space-y-3 sm:space-y-4">
							{/* Share Button */}
							<button
								type="button"
								onClick={sharePayment}
								className="w-full bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2 text-base sm:text-lg touch-manipulation min-h-[56px]"
							>
								<Share2 className="w-5 h-5" />
								<span>Share Payment Link</span>
							</button>

							{/* Copy URL */}
							<button
								type="button"
								onClick={copyToClipboard}
								className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-base touch-manipulation min-h-[56px]"
							>
								{copySuccess ? (
									<>
										<CheckCircle className="w-5 h-5 text-green-600" />
										<span>Copied!</span>
									</>
								) : (
									<>
										<Copy className="w-5 h-5" />
										<span>Copy Link</span>
									</>
								)}
							</button>

							<a
								href={payment.paymentUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-base touch-manipulation min-h-[56px]"
							>
								<ExternalLink className="w-5 h-5" />
								<span>See Payment</span>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
